import React, { useState, useEffect } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { useAuthStore } from '../../stores/authStore'
import { usePermissions } from '../../hooks/usePermissions'
import PageHelp from '../../components/PageHelp'

interface RecurringInvoice {
  id: string
  name: string
  description?: string
  frequency: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'YEARLY'
  startDate: string
  endDate?: string
  nextExecutionDate: string
  isActive: boolean
  reference?: string
  executionCount: number
  lastExecutedAt?: string
  clientOrg: {
    id: string
    name: string
  }
  currency: string
  items: RecurringInvoiceItem[]
  creator: {
    firstName: string
    lastName: string
  }
  generatedInvoices?: Array<{
    id: string
    invoiceNumber: string
    invoiceDate: string
    status: string
  }>
}

interface RecurringInvoiceItem {
  id: string
  description: string
  quantity: string
  unitPrice: string
  taxRateId?: string
  accountId?: string
  costCenterId?: string
  taxRate?: {
    name: string
    rate: string
  }
  account?: {
    code: string
    name: string
  }
  costCenter?: {
    name: string
  }
}

interface ClientOrganization {
  id: string
  name: string
}

interface Account {
  id: string
  code: string
  name: string
  rootType: string
}

interface TaxRate {
  id: string
  name: string
  rate: string
  taxType: string
}

interface CostCenter {
  id: string
  name: string
}

const RecurringInvoices: React.FC = () => {
  const { user } = useAuthStore()
  const { hasPermission, loading: permissionsLoading } = usePermissions(user)
  const [invoices, setInvoices] = useState<RecurringInvoice[]>([])
  const [clientOrgs, setClientOrgs] = useState<ClientOrganization[]>([])
  const [accounts, setAccounts] = useState<Account[]>([])
  const [taxRates, setTaxRates] = useState<TaxRate[]>([])
  const [costCenters, setCostCenters] = useState<CostCenter[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [selectedInvoice, setSelectedInvoice] = useState<RecurringInvoice | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    frequency: 'MONTHLY' as 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'YEARLY',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    reference: '',
    clientOrgId: '',
    currency: 'GBP',
    notes: '',
    terms: '',
    paymentTerms: '',
    items: [
      { description: '', quantity: '1', unitPrice: '', taxRateId: '', accountId: '', costCenterId: '' },
    ] as Array<{ description: string; quantity: string; unitPrice: string; taxRateId: string; accountId: string; costCenterId: string }>,
  })

  useEffect(() => {
    fetchInvoices()
    fetchClientOrgs()
    fetchAccounts()
    fetchTaxRates()
    fetchCostCenters()
  }, [])

  const fetchInvoices = async () => {
    try {
      setIsLoading(true)
      const response = await axios.get('/api/erp/recurring-invoices', {
        params: { includeInactive: 'true' },
      })
      setInvoices(response.data.data || [])
    } catch (error: any) {
      console.error('Error fetching recurring invoices:', error)
      toast.error('Failed to load recurring invoices')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchClientOrgs = async () => {
    try {
      const response = await axios.get('/api/client-organizations')
      setClientOrgs(response.data.data || [])
    } catch (error) {
      console.error('Error fetching client organizations:', error)
    }
  }

  const fetchAccounts = async () => {
    try {
      const response = await axios.get('/api/erp/accounts', { params: { isActive: 'true', rootType: 'REVENUE' } })
      setAccounts(response.data.data || [])
    } catch (error) {
      console.error('Error fetching accounts:', error)
    }
  }

  const fetchTaxRates = async () => {
    try {
      const response = await axios.get('/api/erp/tax-rates', { params: { isActive: 'true' } })
      setTaxRates(response.data.data || [])
    } catch (error) {
      console.error('Error fetching tax rates:', error)
    }
  }

  const fetchCostCenters = async () => {
    try {
      const response = await axios.get('/api/erp/cost-centers', { params: { isActive: 'true' } })
      setCostCenters(response.data.data || [])
    } catch (error) {
      console.error('Error fetching cost centers:', error)
    }
  }

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: '1', unitPrice: '', taxRateId: '', accountId: '', costCenterId: '' }],
    })
  }

  const removeItem = (index: number) => {
    if (formData.items.length > 1) {
      setFormData({
        ...formData,
        items: formData.items.filter((_, i) => i !== index),
      })
    }
  }

  const updateItem = (index: number, field: string, value: string) => {
    const newItems = [...formData.items]
    newItems[index] = { ...newItems[index], [field]: value }
    setFormData({ ...formData, items: newItems })
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.name || !formData.clientOrgId || formData.items.length === 0) {
      toast.error('Please fill in all required fields')
      return
    }

    // Validate items
    for (const item of formData.items) {
      if (!item.description || !item.quantity || !item.unitPrice) {
        toast.error('Please fill in all item fields')
        return
      }
    }

    try {
      await axios.post('/api/erp/recurring-invoices', {
        name: formData.name,
        description: formData.description || undefined,
        frequency: formData.frequency,
        startDate: formData.startDate,
        endDate: formData.endDate || undefined,
        reference: formData.reference || undefined,
        clientOrgId: formData.clientOrgId,
        currency: formData.currency,
        notes: formData.notes || undefined,
        terms: formData.terms || undefined,
        paymentTerms: formData.paymentTerms || undefined,
        items: formData.items.map(item => ({
          description: item.description,
          quantity: parseFloat(item.quantity),
          unitPrice: parseFloat(item.unitPrice),
          taxRateId: item.taxRateId || undefined,
          accountId: item.accountId || undefined,
          costCenterId: item.costCenterId || undefined,
        })),
      })
      toast.success('Recurring invoice created successfully!')
      setShowCreateModal(false)
      resetForm()
      fetchInvoices()
    } catch (error: any) {
      console.error('Error creating recurring invoice:', error)
      toast.error(error.response?.data?.error || 'Failed to create recurring invoice')
    }
  }

  const handlePause = async (id: string) => {
    try {
      await axios.post(`/api/erp/recurring-invoices/${id}/pause`)
      toast.success('Recurring invoice paused')
      fetchInvoices()
    } catch (error: any) {
      console.error('Error pausing recurring invoice:', error)
      toast.error(error.response?.data?.error || 'Failed to pause recurring invoice')
    }
  }

  const handleResume = async (id: string) => {
    try {
      await axios.post(`/api/erp/recurring-invoices/${id}/resume`)
      toast.success('Recurring invoice resumed')
      fetchInvoices()
    } catch (error: any) {
      console.error('Error resuming recurring invoice:', error)
      toast.error(error.response?.data?.error || 'Failed to resume recurring invoice')
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this recurring invoice? This can only be done if no invoices have been generated yet.')) {
      return
    }

    try {
      await axios.delete(`/api/erp/recurring-invoices/${id}`)
      toast.success('Recurring invoice deleted')
      fetchInvoices()
    } catch (error: any) {
      console.error('Error deleting recurring invoice:', error)
      toast.error(error.response?.data?.error || 'Failed to delete recurring invoice')
    }
  }

  const handleView = async (id: string) => {
    try {
      setIsLoading(true)
      const response = await axios.get(`/api/erp/recurring-invoices/${id}`)
      setSelectedInvoice(response.data.data)
      setShowViewModal(true)
    } catch (error: any) {
      console.error('Error fetching recurring invoice:', error)
      toast.error('Failed to load recurring invoice details')
    } finally {
      setIsLoading(false)
    }
  }

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      frequency: 'MONTHLY',
      startDate: new Date().toISOString().split('T')[0],
      endDate: '',
      reference: '',
      clientOrgId: '',
      currency: 'GBP',
      notes: '',
      terms: '',
      paymentTerms: '',
      items: [{ description: '', quantity: '1', unitPrice: '', taxRateId: '', accountId: '', costCenterId: '' }],
    })
  }

  const getFrequencyLabel = (freq: string) => {
    const labels: Record<string, string> = {
      DAILY: 'Daily',
      WEEKLY: 'Weekly',
      MONTHLY: 'Monthly',
      QUARTERLY: 'Quarterly',
      YEARLY: 'Yearly',
    }
    return labels[freq] || freq
  }

  if (permissionsLoading) {
    return <div className="p-6">Loading...</div>
  }

  if (!hasPermission('erp_invoice', 'read')) {
    return (
      <div className="p-6">
        <p className="text-red-800">You don't have permission to view recurring invoices.</p>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900">Recurring Invoices</h1>
          <PageHelp pageIdentifier="recurring-invoices" title="Recurring Invoices" />
        </div>
        {hasPermission('erp_invoice', 'write') && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            Create Recurring Invoice
          </button>
        )}
      </div>

      {isLoading ? (
        <p className="mt-2 text-gray-600">Loading recurring invoices...</p>
      ) : invoices.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <p className="text-gray-600">No recurring invoices found. Create your first recurring invoice to get started.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Frequency</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Next Execution</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Executions</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {invoices.map((invoice) => (
                <tr key={invoice.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{invoice.name}</div>
                    {invoice.description && (
                      <div className="text-sm text-gray-500">{invoice.description}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{invoice.clientOrg.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{getFrequencyLabel(invoice.frequency)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(invoice.nextExecutionDate).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{invoice.executionCount}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${invoice.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {invoice.isActive ? 'Active' : 'Paused'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleView(invoice.id)}
                      className="text-indigo-600 hover:text-indigo-900 mr-3"
                    >
                      View
                    </button>
                    {invoice.isActive ? (
                      hasPermission('erp_invoice', 'write') && (
                        <button
                          onClick={() => handlePause(invoice.id)}
                          className="text-yellow-600 hover:text-yellow-900 mr-3"
                        >
                          Pause
                        </button>
                      )
                    ) : (
                      hasPermission('erp_invoice', 'write') && (
                        <button
                          onClick={() => handleResume(invoice.id)}
                          className="text-green-600 hover:text-green-900 mr-3"
                        >
                          Resume
                        </button>
                      )
                    )}
                    {hasPermission('erp_invoice', 'delete') && (
                      <button
                        onClick={() => handleDelete(invoice.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex justify-center items-center">
          <div className="bg-white p-8 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6 text-gray-900">Create Recurring Invoice</h2>
            <form onSubmit={handleCreate}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Client Organization <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.clientOrgId}
                    onChange={(e) => setFormData({ ...formData, clientOrgId: e.target.value })}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="">Select Client</option>
                    {clientOrgs.map(org => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Frequency <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.frequency}
                    onChange={(e) => setFormData({ ...formData, frequency: e.target.value as any })}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="DAILY">Daily</option>
                    <option value="WEEKLY">Weekly</option>
                    <option value="MONTHLY">Monthly</option>
                    <option value="QUARTERLY">Quarterly</option>
                    <option value="YEARLY">Yearly</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Start Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">End Date (Optional)</label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Currency</label>
                  <select
                    value={formData.currency}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="GBP">GBP</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                  </select>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                ></textarea>
              </div>

              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Invoice Items <span className="text-red-500">*</span>
                  </label>
                  <button
                    type="button"
                    onClick={addItem}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    + Add Item
                  </button>
                </div>
                <div className="space-y-2">
                  {formData.items.map((item, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 items-end border p-2 rounded">
                      <div className="col-span-4">
                        <input
                          type="text"
                          placeholder="Description"
                          value={item.description}
                          onChange={(e) => updateItem(index, 'description', e.target.value)}
                          required
                          className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
                        />
                      </div>
                      <div className="col-span-2">
                        <input
                          type="number"
                          step="0.01"
                          placeholder="Quantity"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                          required
                          className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
                        />
                      </div>
                      <div className="col-span-2">
                        <input
                          type="number"
                          step="0.01"
                          placeholder="Unit Price"
                          value={item.unitPrice}
                          onChange={(e) => updateItem(index, 'unitPrice', e.target.value)}
                          required
                          className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
                        />
                      </div>
                      <div className="col-span-2">
                        <select
                          value={item.taxRateId}
                          onChange={(e) => updateItem(index, 'taxRateId', e.target.value)}
                          className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
                        >
                          <option value="">No Tax</option>
                          {taxRates.map(rate => (
                            <option key={rate.id} value={rate.id}>{rate.name} ({rate.rate}%)</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-span-2 flex gap-1">
                        {formData.items.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            className="text-red-600 hover:text-red-800 text-sm"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false)
                    resetForm()
                  }}
                  className="bg-gray-300 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                >
                  Create Recurring Invoice
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedInvoice && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex justify-center items-center">
          <div className="bg-white p-8 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6 text-gray-900">Recurring Invoice Details</h2>
            
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <p className="mt-1 text-sm text-gray-900">{selectedInvoice.name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Client</label>
                <p className="mt-1 text-sm text-gray-900">{selectedInvoice.clientOrg.name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Frequency</label>
                <p className="mt-1 text-sm text-gray-900">{getFrequencyLabel(selectedInvoice.frequency)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Next Execution</label>
                <p className="mt-1 text-sm text-gray-900">{new Date(selectedInvoice.nextExecutionDate).toLocaleDateString()}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <p className="mt-1">
                  <span className={`px-2 py-1 text-xs rounded-full ${selectedInvoice.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                    {selectedInvoice.isActive ? 'Active' : 'Paused'}
                  </span>
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Executions</label>
                <p className="mt-1 text-sm text-gray-900">{selectedInvoice.executionCount}</p>
              </div>
            </div>

            {selectedInvoice.description && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <p className="mt-1 text-sm text-gray-900">{selectedInvoice.description}</p>
              </div>
            )}

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Invoice Items</label>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Quantity</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Unit Price</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tax Rate</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {selectedInvoice.items.map((item, index) => (
                    <tr key={index}>
                      <td className="px-4 py-2 text-sm text-gray-900">{item.description}</td>
                      <td className="px-4 py-2 text-sm text-gray-500 text-right">{item.quantity}</td>
                      <td className="px-4 py-2 text-sm text-gray-500 text-right">
                        {parseFloat(item.unitPrice).toLocaleString('en-GB', { style: 'currency', currency: selectedInvoice.currency })}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-500">{item.taxRate?.name || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {selectedInvoice.generatedInvoices && selectedInvoice.generatedInvoices.length > 0 && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Generated Invoices</label>
                <div className="space-y-1">
                  {selectedInvoice.generatedInvoices.map((inv) => (
                    <div key={inv.id} className="text-sm text-gray-600">
                      {inv.invoiceNumber} - {new Date(inv.invoiceDate).toLocaleDateString()} ({inv.status})
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowViewModal(false)
                  setSelectedInvoice(null)
                }}
                className="bg-gray-300 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-400"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default RecurringInvoices
